import { calculateWeightedOverallScore } from '../../utils/scoreCalculator';

/**
 * Candidate table sorting, shared by the recruiter screening page and the public
 * hiring-manager review page.
 *
 * Extracted verbatim from ScreeningDetailPage (getOverallScoreNumber /
 * sortByOverallDesc) rather than reimplemented: the public review page was
 * rendering candidates in raw server order while the recruiter sorted them, so
 * the two surfaces ranked the same shortlist differently. A second copy of this
 * logic would just drift again on the next scoring change.
 *
 * `weightsConfig` matters as much as the comparator: with it null,
 * calculateWeightedOverallScore falls back to a strict unweighted average, so an
 * org that weights interview above resume gets a different overall score — and
 * therefore a different ranking — than its own recruiters see.
 */

/**
 * Overall score for one candidate, honouring the screening's enabled stages and
 * the org's configured score weights.
 *
 * @param {object} candidate
 * @param {object} screeningMeta - needs hasResumeScoring / hasAssessment / hasInterview
 * @param {object|null} weightsConfig - org scoreWeights; null ⇒ unweighted average
 * @returns {number|null} null when the candidate has no scores at all
 */
export function getOverallScoreNumber(candidate, screeningMeta, weightsConfig) {
  const scoresObj = {};
  if (screeningMeta?.hasResumeScoring !== false) {
    scoresObj.resume = candidate.resumeScore;
  }
  if (screeningMeta?.hasAssessment) {
    let assessmentScore = null;
    if (candidate.assessmentScore != null) {
      const totalPoints = candidate.assessmentTotalPoints;
      if (totalPoints && totalPoints > 0) {
        assessmentScore = Math.round((candidate.assessmentScore / totalPoints) * 100);
      } else {
        assessmentScore = candidate.assessmentScore;
      }
    }
    scoresObj.assessment = assessmentScore;
  }
  if (screeningMeta?.hasInterview) {
    scoresObj.interview = candidate.interviewScore;
  }
  return calculateWeightedOverallScore(scoresObj, weightsConfig);
}

/**
 * Build the candidate comparator.
 *
 * Candidates with no value for the sort column always sink below those that have
 * one (regardless of direction), and ties break alphabetically by name — so the
 * order is stable and "—" rows never float to the top.
 *
 * @returns {(a: object, b: object) => number}
 */
export function buildCandidateComparator({
  screeningMeta,
  weightsConfig = null,
  sortColumn = 'overallScore',
  sortDirection = 'desc',
} = {}) {
  const overallOf = (c) => getOverallScoreNumber(c, screeningMeta, weightsConfig);

  return (a, b) => {
    let aVal, bVal;
    switch (sortColumn) {
      case 'resumeScore':
        aVal = a.resumeScore ?? -1;
        bVal = b.resumeScore ?? -1;
        break;
      case 'interviewScore':
        aVal = a.interviewScore ?? -1;
        bVal = b.interviewScore ?? -1;
        break;
      case 'assessmentScore':
        aVal = a.assessmentScore ?? -1;
        bVal = b.assessmentScore ?? -1;
        break;
      case 'completedAt':
        aVal = a.completedAt ? new Date(a.completedAt).getTime() : -1;
        bVal = b.completedAt ? new Date(b.completedAt).getTime() : -1;
        break;
      case 'appliedDate':
        aVal = (a.createdAt || a.appliedAt) ? new Date(a.createdAt || a.appliedAt).getTime() : -1;
        bVal = (b.createdAt || b.appliedAt) ? new Date(b.createdAt || b.appliedAt).getTime() : -1;
        break;
      // Offered by CandidateColumnsModal on the Selected / Rejected tabs; without
      // these cases they silently fell through to overall score.
      case 'shortlistedDate':
        aVal = a.shortlistedAt ? new Date(a.shortlistedAt).getTime() : -1;
        bVal = b.shortlistedAt ? new Date(b.shortlistedAt).getTime() : -1;
        break;
      case 'rejectedDate':
        aVal = a.rejectedAt ? new Date(a.rejectedAt).getTime() : -1;
        bVal = b.rejectedAt ? new Date(b.rejectedAt).getTime() : -1;
        break;
      case 'overallScore':
      default:
        aVal = overallOf(a) ?? -1;
        bVal = overallOf(b) ?? -1;
        break;
    }

    const aHas = aVal !== -1;
    const bHas = bVal !== -1;

    if (aHas && bHas) {
      if (aVal !== bVal) {
        if (sortDirection === 'asc') {
          return aVal < bVal ? -1 : 1;
        }
        return aVal < bVal ? 1 : -1;
      }
    } else if (aHas && !bHas) {
      return -1;
    } else if (!aHas && bHas) {
      return 1;
    }

    const aName = (a?.name || '').toLowerCase();
    const bName = (b?.name || '').toLowerCase();
    if (aName < bName) return -1;
    if (aName > bName) return 1;
    return 0;
  };
}
